import type { ReactNode } from 'react';
import type { Theme } from '@mui/material/styles';
import type { PhoneBillFileEntry, PhoneFormValues } from '@t/checkout.types';
import { getFileIconName } from '@utils/fileIconName';
import type { FilePreviewViewModel } from '@components/Checkout/steps/PhoneNumberStep/PhoneNumberStep.types';

export const DIRECTORY_LISTING_DESCRIPTIONS: Record<string, string> = {
  Listed: 'Name, address and phone # will be in DA (411) and DL (phone book) listing',
  'Non-Listed': 'Name, address and phone # will not be in DA (411) or DL (phone book)',
  'Non-Published':
    'Name, address and phone # will not appear in DL (phone book) but will be available through DA (411)',
};

export const INITIAL_VALUES: PhoneFormValues = {
  choice: 'NEWNUMBER',
  portInNumber: '',
  portInProvider: '',
  portInProviderOther: '',
  accountNumber: '',
  nameOnBill: '',
  directoryListing: 'Listed',
  phoneBillFiles: [],
  existingPhoneBillFiles: [],
};

export const textFieldSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-input::placeholder': { fontSize: '16px', fontWeight: 400, lineHeight: '100%' },
  '& .MuiOutlinedInput-root': { '& fieldset': { borderRadius: '6px' } },
  '& .MuiOutlinedInput-input:-webkit-autofill': {
    WebkitBoxShadow: `0 0 0 100px ${theme.colors.white} inset`,
    WebkitTextFillColor: 'inherit',
  },
});

export const buildDirectoryLabel = (name: string): ReactNode => {
  const description = DIRECTORY_LISTING_DESCRIPTIONS[name];
  if (!description) return name;
  return (
    <span>
      <strong>{name}</strong> - {description}
    </span>
  );
};

export const truncateFileName = (name: string): string => (name.length > 20 ? `${name.slice(0, 20)}...` : name);

export const ACCEPTED_BILL_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;

type AcceptedBillMimeType = (typeof ACCEPTED_BILL_MIME_TYPES)[number];

export const MAX_BILL_FILE_BYTES = 1024 * 1024;

type PhoneBillFileValidation = { ok: true } | { ok: false; message: string };

export const validatePhoneBillFile = (file: File): PhoneBillFileValidation => {
  if (!ACCEPTED_BILL_MIME_TYPES.includes(file.type as AcceptedBillMimeType)) {
    return { ok: false, message: 'Phone bill must be a PDF, JPEG, or PNG file.' };
  }
  if (file.size > MAX_BILL_FILE_BYTES) {
    return { ok: false, message: 'Phone bill must be 1 MB or smaller.' };
  }
  return { ok: true };
};

export const formatFileDate = (lastModified: number): string =>
  new Date(lastModified).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

export const fileToPreviewViewModel = (file: File): FilePreviewViewModel => ({
  key: `${file.name}-${file.lastModified}`,
  displayName: file.name,
  iconName: getFileIconName(file.name),
  dateLabel: formatFileDate(file.lastModified),
});

export const serverFileToPreviewViewModel = (entry: PhoneBillFileEntry): FilePreviewViewModel => ({
  key: entry.fileId,
  displayName: entry.fileName,
  iconName: getFileIconName(entry.fileName),
});
